import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import { StoredUser, getStoredUser, activateQuickSearch, refreshUserProfile } from '../services/subscriptionService';
import buttonStyles from '../styles/modules/Button.module.css';
import modalStyles from '../styles/modules/Modal.module.css';
import confirmDialogStyles from '../styles/modules/ConfirmDialog.module.css';
import styles from '../styles/modules/TokenUsageModal.module.css';

type TokenType = 'quickSearch' | 'jobPost';

interface TokenUsageModalProps {
    type: TokenType;
    isOpen: boolean;
    onClose: () => void;
    onTokenUsed?: () => void;
}

const TokenUsageModal: React.FC<TokenUsageModalProps> = ({
    type,
    isOpen,
    onClose,
    onTokenUsed
}) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [isActivating, setIsActivating] = useState(false);
    const [isRefreshingProfile, setIsRefreshingProfile] = useState(false);
    const [serverUser, setServerUser] = useState<StoredUser | null>(null);

    const user = serverUser || getStoredUser();
    const availableCount = type === 'quickSearch' ? (user?.quickSearchCount ?? 0) : (user?.jobPostsCount ?? 0);

    const refreshProfileFromServer = async (): Promise<StoredUser | null> => {
        const token = localStorage.getItem('authToken');

        if (!token) {
            return null;
        }

        try {
            setIsRefreshingProfile(true);
            const latestUser = await refreshUserProfile(token);
            setServerUser(latestUser);
            return latestUser;
        } catch (error) {
            console.error('Failed to refresh profile for token modal:', error);
            return getStoredUser();
        } finally {
            setIsRefreshingProfile(false);
        }
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        refreshProfileFromServer();
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    const handleUseToken = async () => {
        const latestUser = await refreshProfileFromServer();
        const latestAvailableCount = type === 'quickSearch'
            ? (latestUser?.quickSearchCount ?? 0)
            : (latestUser?.jobPostsCount ?? 0);

        if (latestAvailableCount === 0) {
            // No tokens, go to billing
            onClose();
            navigate('/settings?tab=billing');
            return;
        }

        setIsActivating(true);

        try {
            if (type === 'quickSearch') {
                const token = localStorage.getItem('authToken');
                if (!token) {
                    throw new Error('Not authenticated');
                }

                await activateQuickSearch(token);

                // Refresh user profile to get updated counts
                await refreshUserProfile(token);

                showToast({
                    message: t('tokenUsage.quickSearchActivated') || 'Quick Search activated! You can now search for jobs for the next 24 hours.',
                    type: 'success'
                });

                onTokenUsed?.();
                onClose();
            } else {
                // For job posts, just show success and close
                // The actual consumption happens when creating the job offer
                showToast({
                    message: t('tokenUsage.jobPostReady') || 'You can now create a job offer.',
                    type: 'success'
                });

                onTokenUsed?.();
                onClose();
            }
        } catch (error) {
            showToast({
                message: error instanceof Error ? error.message : (t('tokenUsage.activationFailed') || 'Failed to activate token'),
                type: 'error'
            });
        } finally {
            setIsActivating(false);
        }
    };

    const titleKey = type === 'quickSearch' ? 'tokenUsage.quickSearchTitle' : 'tokenUsage.jobPostTitle';
    const messageKey = availableCount === 0
        ? (type === 'quickSearch' ? 'tokenUsage.noQuickSearchCredits' : 'tokenUsage.noJobPostCredits')
        : (type === 'quickSearch' ? 'tokenUsage.quickSearchCreditsAvailable' : 'tokenUsage.jobPostCreditsAvailable');

    return (
        <div className={`${modalStyles.overlay} ${styles.overlay}`} onClick={onClose}>
            <div className={`${modalStyles.modal} ${styles.modal}`} onClick={(e) => e.stopPropagation()}>
                <div className={`${modalStyles.header} ${styles.header}`}>
                    <h2 className={styles.title}>{t(titleKey)}</h2>
                    <button
                        className={`${modalStyles.closeButton} ${styles.closeButton}`}
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                <div className={`${modalStyles.body} ${styles.body}`}>
                    <p className={styles.message}>
                        {t(messageKey, { count: availableCount }) || 
                         (availableCount > 0 
                             ? `You have ${availableCount} ${type === 'quickSearch' ? 'Quick Search' : 'Job Post'} ${availableCount === 1 ? 'credit' : 'credits'} available.`
                             : `You don't have any ${type === 'quickSearch' ? 'Quick Search' : 'Job Post'} credits.`)}
                    </p>

                    <p className={styles.info}>
                        {type === 'quickSearch'
                            ? (t('tokenUsage.quickSearchInfo') || 'Using a Quick Search credit will open a 24-hour search window.')
                            : (t('tokenUsage.jobPostInfo') || 'Using a Job Post credit will allow you to create one job offer.')}
                    </p>
                </div>

                <div className={`${modalStyles.footer} ${styles.footer}`}>
                    <button
                        type="button"
                        className={`${confirmDialogStyles.cancelBtn} ${styles.cancelButton}`}
                        onClick={onClose}
                    >
                        {t('common.cancel') || 'Cancel'}
                    </button>

                    <button
                        type="button"
                        className={`${buttonStyles.success} ${styles.actionButton}`}
                        onClick={handleUseToken}
                            disabled={isActivating || isRefreshingProfile}
                    >
                            {(isActivating || isRefreshingProfile)
                            ? (t('common.loading') || 'Loading...')
                            : (availableCount === 0
                                ? (t('tokenUsage.buyCredits') || 'Buy Credits')
                                : (t('tokenUsage.useCredit') || 'Use 1 Credit'))}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TokenUsageModal;
